import React, { useState, useEffect, useCallback } from 'react';

const API = '/api/reviews';
function getToken() { return localStorage.getItem('authToken') || ''; }
function headers() { return { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }; }

function timeAgo(d: string) {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h}h`;
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function statusOf(r: any) {
  if (r.has_reviewed) return { label: 'Avis laissé ✓', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' };
  if (r.email_clicked || r.sms_clicked) return { label: 'Lien cliqué', cls: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-400' };
  if (r.email_sent || r.sms_sent) return { label: 'Envoyé', cls: 'bg-violet-100 text-violet-700 border-violet-200', dot: 'bg-violet-400' };
  return { label: 'En attente', cls: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-300' };
}

export default function ReviewsPage() {
  const [stats, setStats] = useState<any>(null);
  const [qrData, setQrData] = useState<any>(null);
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [copied, setCopied]           = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [posterFormat, setPosterFormat] = useState<'A4' | 'A5' | 'A6'>('A5');
  const [sendMode, setSendMode] = useState<'email' | 'sms'>('email');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [form, setForm] = useState({ nom: '', email: '', phone: '' });

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter !== 'all') params.set('filter', filter);
    if (search) params.set('search', search);
    const [statsRes, qrRes, listRes] = await Promise.allSettled([
      fetch(`${API}/stats`, { headers: headers() }).then(r => r.json()),
      fetch(`${API}/qr`, { headers: headers() }).then(r => r.json()),
      fetch(`${API}?${params}`, { headers: headers() }).then(r => r.json()),
    ]);
    if (statsRes.status === 'fulfilled' && statsRes.value.success) { setStats(statsRes.value.stats); if (statsRes.value.stats.businessName) setBusinessName(statsRes.value.stats.businessName); }
    if (qrRes.status === 'fulfilled' && qrRes.value.success) setQrData(qrRes.value);
    if (listRes.status === 'fulfilled' && listRes.value.success) setRequests(listRes.value.requests);
    setLoading(false);
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setSendResult(null);
    try {
      const res = await fetch(`${API}/send`, { method: 'POST', headers: headers(), body: JSON.stringify(form) });
      const json = await res.json();
      if (!res.ok || !json.success) {
        setSendResult({ type: 'error', msg: json.error || "Erreur d'envoi." });
      } else {
        const parts: string[] = [];
        if (json.results.email === 'sent') parts.push('Email envoyé ✓');
        if (json.results.sms === 'sent') parts.push('SMS envoyé ✓');
        if (json.results.sms === 'quota_exceeded') parts.push('Quota SMS atteint');
        const smsErr = typeof json.results.sms === 'string' && json.results.sms.startsWith('error:');
        const emailErr = typeof json.results.email === 'string' && json.results.email.startsWith('error:');
        if (smsErr) setSendResult({ type: 'error', msg: `Échec SMS : ${json.results.sms.replace('error: ', '')}` });
        else if (emailErr) setSendResult({ type: 'error', msg: `Échec email : ${json.results.email.replace('error: ', '')}` });
        else setSendResult({ type: 'success', msg: parts.length ? parts.join(', ') : 'Demande enregistrée.' });
        setForm({ nom: '', email: '', phone: '' });
        load();
      }
    } catch {
      setSendResult({ type: 'error', msg: 'Erreur réseau.' });
    }
    setSending(false);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Supprimer cette demande ?')) return;
    await fetch(`${API}/${id}`, { method: 'DELETE', headers: headers() });
    load();
  };

  const handleReviewed = async (id: number) => {
    await fetch(`${API}/${id}/reviewed`, { method: 'PATCH', headers: headers() });
    load();
  };

  const copyLink = () => {
    if (qrData?.reviewUrl) {
      navigator.clipboard.writeText(qrData.reviewUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const generatePoster = () => {
    if (!qrData?.qrDataUrl) return;

    // Dimensions de carte et disposition sur A4
    const configs: Record<string, { cw: number; ch: number; cols: number; rows: number; fs: number; logoSz: number; starSz: number; qrSz: number }> = {
      A4: { cw: 190, ch: 272, cols: 1, rows: 1, fs: 26,  logoSz: 72, starSz: 38, qrSz: 130 },
      A5: { cw: 92,  ch: 130, cols: 2, rows: 2, fs: 13,  logoSz: 38, starSz: 19, qrSz: 62  },
      A6: { cw: 86,  ch: 122, cols: 2, rows: 2, fs: 11.5,logoSz: 34, starSz: 17, qrSz: 56  },
    };
    const c = configs[posterFormat];
    const googleSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 262" width="${c.logoSz}" height="${c.logoSz}" style="display:block;margin:0 auto">
      <path fill="#4285F4" d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"/>
      <path fill="#34A853" d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"/>
      <path fill="#FBBC05" d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"/>
      <path fill="#EB4335" d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"/>
    </svg>`;

    const card = () => `
    <div style="width:${c.cw}mm;height:${c.ch}mm;border:1.5px solid #e0e0e0;border-radius:5mm;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 2px 8px rgba(0,0,0,.12);page-break-inside:avoid;">
      <div style="flex:1;background:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${c.fs * 0.5}px;padding:${c.fs}px;">
        ${googleSvg}
        <div style="color:#F9A825;font-size:${c.starSz}px;letter-spacing:4px;line-height:1;">&#9733;&#9733;&#9733;&#9733;&#9733;</div>
        <div style="font-size:${c.fs * 1.15}px;font-weight:900;color:#1a1a1a;text-align:center;letter-spacing:2px;line-height:1.2;text-transform:uppercase;">Laissez-nous<br>un avis</div>
        <div style="font-size:${c.fs * 0.6}px;color:#666;text-align:center;line-height:1.5;text-transform:uppercase;letter-spacing:1px;">Scannez le QR code<br>avec votre t&eacute;l&eacute;phone</div>
      </div>
      <div style="background:#F9A825;display:flex;align-items:center;justify-content:center;padding:${c.fs * 0.9}px;flex:0 0 42%;">
        <div style="background:#fff;border-radius:4mm;padding:${c.fs * 0.5}px;box-shadow:0 4px 16px rgba(0,0,0,.2);">
          <img src="${qrData!.qrDataUrl}" style="display:block;width:${c.qrSz}mm;height:${c.qrSz}mm;"/>
        </div>
      </div>
    </div>`;

    const totalCards = c.cols * c.rows;
    const cards = Array(totalCards).fill(null).map(() => card()).join('');
    const gridStyle = c.cols > 1
      ? `display:grid;grid-template-columns:repeat(${c.cols},${c.cw}mm);gap:5mm;justify-content:center;align-content:center;`
      : `display:flex;justify-content:center;align-items:center;`;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Affiche Avis Google ${posterFormat}</title>
<style>
  @page { size: A4; margin: 10mm; }
  * { margin:0; padding:0; box-sizing:border-box; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  body { font-family: Arial, Helvetica, sans-serif; min-height:100vh; display:flex; align-items:center; justify-content:center; }
  .grid { ${gridStyle} gap:5mm; }
</style></head><body>
<div class="grid">${cards}</div>
<script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
</body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  const downloadQr = () => {
    if (!qrData?.qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrData.qrDataUrl;
    a.download = 'qr-avis-google.png';
    a.click();
  };

  // SMS preview text
  const smsPreview = form.phone
    ? `${businessName || '[Votre commerce]'} vous remercie ! Laissez votre avis Google ici : https://app.evoclick.fr/api/reviews/r/[token]?src=sms`
    : '';

  const filterTabs = [
    { key: 'all', label: 'Tous' },
    { key: 'email', label: 'Email' },
    { key: 'sms', label: 'SMS' },
    { key: 'clicked', label: 'Cliqués' },
    { key: 'reviewed', label: 'Avis laissé' },
  ];

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="animate-spin inline-block w-10 h-10 border-4 border-violet-500 border-t-transparent rounded-full mb-4" />
        <p className="text-gray-500 text-sm">Chargement…</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">

      {/* Toast résultat */}
      {sendResult && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-xl border text-sm font-medium transition-all ${
          sendResult.type === 'success'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <span>{sendResult.type === 'success' ? '✅' : '❌'}</span>
          {sendResult.msg}
          <button onClick={() => setSendResult(null)} className="ml-2 text-gray-400 hover:text-gray-600">✕</button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Demandes envoyées', value: stats?.total ?? 0, grad: 'from-violet-600 to-indigo-600', icon: '📤' },
          { label: 'Liens cliqués', value: stats?.clicked ?? 0, grad: 'from-blue-500 to-sky-600', icon: '👆' },
          { label: 'Avis obtenus', value: stats?.reviewed ?? 0, grad: 'from-emerald-500 to-teal-600', icon: '⭐' },
          { label: 'SMS restants', value: stats?.smsRemaining ?? 0, sub: `sur ${stats?.smsMax ?? 20}/mois`, grad: 'from-amber-500 to-orange-500', icon: '📱', alert: (stats?.smsRemaining ?? 0) <= 3 },
        ].map(k => (
          <div key={k.label} className={`rounded-2xl bg-gradient-to-br ${k.grad} p-5 text-white shadow-md relative overflow-hidden`}>
            <div className="absolute top-3 right-4 text-2xl opacity-30">{k.icon}</div>
            <p className="text-white/70 text-[11px] font-bold uppercase tracking-widest mb-1">{k.label}</p>
            <p className="text-4xl font-black">{k.value}</p>
            {'sub' in k && <p className="text-white/60 text-xs mt-0.5">{k.sub}</p>}
            {k.alert && k.value <= 3 && (
              <p className="text-white/80 text-[10px] mt-1 font-semibold">⚠️ Quota bientôt atteint</p>
            )}
          </div>
        ))}
      </div>

      {/* Outils + form */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* QR Code + lien */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-3 flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-white"><path d="M3 3h6v6H3zM15 3h6v6h-6zM3 15h6v6H3z"/><rect x="15" y="15" width="3" height="3"/><rect x="18" y="18" width="3" height="3"/><rect x="15" y="18" width="3" height="3"/><rect x="18" y="15" width="3" height="3"/></svg>
            <h3 className="font-bold text-white text-sm">Votre QR Code & lien Google</h3>
          </div>
          <div className="p-5 flex flex-col items-center gap-4">
            {qrData?.qrDataUrl ? (
              <img src={qrData.qrDataUrl} alt="QR Code" className="w-40 h-40 rounded-2xl border-4 border-gray-100 shadow-sm" />
            ) : (
              <div className="w-40 h-40 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400">
                <p className="text-xs text-center px-4">Connectez Google My Business</p>
              </div>
            )}
            <div className="w-full space-y-2">
              <button onClick={copyLink} disabled={!qrData?.reviewUrl}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all border ${
                  copied ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}>
                {copied ? '✅ Lien copié !' : (
                  <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg> Copier le lien d'avis</>
                )}
              </button>
              <button onClick={downloadQr} disabled={!qrData?.qrDataUrl}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
                Télécharger le QR Code
              </button>

              {/* Affiche imprimable */}
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <p className="text-gray-500 text-xs text-center font-semibold uppercase tracking-wide">Affiche prête à imprimer</p>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { fmt: 'A4' as const, label: 'Vitrine', sub: '1 grande carte' },
                    { fmt: 'A5' as const, label: 'Comptoir', sub: '4 cartes / page' },
                    { fmt: 'A6' as const, label: 'Table', sub: '4 cartes / page' },
                  ]).map(({ fmt, label, sub }) => (
                    <button key={fmt} onClick={() => setPosterFormat(fmt)}
                      className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all ${posterFormat === fmt ? 'border-yellow-400 bg-yellow-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}>
                      {/* Mini poster preview */}
                      <div className="w-10 rounded overflow-hidden border border-gray-200 shadow-sm" style={{ height: fmt === 'A4' ? '56px' : '48px' }}>
                        <div className="bg-white flex flex-col items-center justify-center gap-0.5 py-1" style={{ height: '60%' }}>
                          <svg viewBox="0 0 256 262" className="w-3 h-3"><path fill="#4285F4" d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"/><path fill="#34A853" d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"/><path fill="#FBBC05" d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"/><path fill="#EB4335" d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"/></svg>
                          <div style={{ fontSize: '6px', color: '#F9A825', letterSpacing: '1px' }}>★★★★★</div>
                        </div>
                        <div className="flex items-center justify-center" style={{ height: '40%', background: '#F9A825' }}>
                          <div className="bg-white rounded-sm" style={{ width: '14px', height: '14px' }} />
                        </div>
                      </div>
                      <span className={`text-xs font-bold leading-none ${posterFormat === fmt ? 'text-yellow-700' : 'text-gray-700'}`}>{label}</span>
                      <span className="text-[9px] text-gray-400 leading-none">{sub}</span>
                    </button>
                  ))}
                </div>
                <button onClick={generatePoster} disabled={!qrData?.qrDataUrl}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-yellow-500 to-amber-500 text-white hover:from-yellow-600 hover:to-amber-600 transition-all shadow-sm disabled:opacity-50">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm1-12V4h6v5"/></svg>
                  Imprimer l'affiche {posterFormat === 'A4' ? 'Vitrine' : posterFormat === 'A5' ? 'Comptoir' : 'Table'}
                </button>
              </div>
            </div>
            {qrData?.reviewUrl && (
              <a href={qrData.reviewUrl} target="_blank" rel="noreferrer"
                className="text-xs text-gray-400 hover:text-blue-600 truncate w-full text-center transition-colors">
                {qrData.reviewUrl}
              </a>
            )}
          </div>
        </div>

        {/* Formulaire d'envoi */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-white"><path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>
              <h3 className="font-bold text-white text-sm">Envoyer une demande d'avis</h3>
            </div>
          </div>
          <div className="p-5">
            <form onSubmit={handleSend} className="space-y-4">
              {/* Toggle Email / SMS */}
              <div className="flex rounded-xl overflow-hidden border border-gray-200 text-sm font-semibold">
                <button type="button" onClick={() => { setSendMode('email'); setForm(f => ({ ...f, phone: '' })); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 transition-colors ${sendMode === 'email' ? 'bg-violet-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                  Par email
                </button>
                <button type="button" onClick={() => { setSendMode('sms'); setForm(f => ({ ...f, email: '' })); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 transition-colors border-l border-gray-200 ${sendMode === 'sms' ? 'bg-violet-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
                  Par SMS
                  {stats && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-normal ${sendMode === 'sms' ? 'bg-white/20 text-white' : 'bg-amber-50 text-amber-600'}`}>{stats.smsRemaining} restants</span>}
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nom du client <span className="text-red-400">*</span></label>
                <input type="text" required placeholder="Jean Dupont"
                  value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-400 transition-all" />
              </div>

              {sendMode === 'email' ? (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Adresse email <span className="text-red-400">*</span></label>
                  <input type="email" required placeholder="client@email.fr"
                    value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-400 transition-all" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Numéro de téléphone <span className="text-red-400">*</span></label>
                    <input type="tel" required placeholder="06 12 34 56 78"
                      value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      className={`w-full px-4 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all ${
                        form.phone && !/^(0[67][\s.\-\/]?\d{2}[\s.\-\/]?\d{2}[\s.\-\/]?\d{2}[\s.\-\/]?\d{2}|(\+33)[67]\d{8})$/.test(form.phone.replace(/[\s.\-\/]/g,'').replace(/^0/,'0'))
                          ? 'border-red-300 focus:ring-red-300'
                          : 'border-gray-200 focus:ring-violet-400'
                      }`} />
                    {form.phone && !/^(0[67]\d{8}|(\+33)[67]\d{8})$/.test(form.phone.replace(/[\s.\-\/]/g, '')) && (
                      <p className="text-red-500 text-[11px] mt-1">Numéro invalide — doit commencer par 06 ou 07</p>
                    )}
                  </div>
                  {form.phone && (
                    <div className="flex gap-4 items-start bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="shrink-0">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Aperçu SMS</p>
                        <div className="w-[130px] bg-white border-4 border-gray-800 rounded-2xl overflow-hidden shadow-md">
                          <div className="bg-gray-200 text-center py-1.5 text-[10px] font-bold text-gray-700 border-b border-gray-300">{businessName || 'AvisClient'}</div>
                          <div className="p-2">
                            <div className="bg-gray-100 rounded-xl p-2 text-[9px] text-gray-700 leading-relaxed break-words">{smsPreview}</div>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 pt-5">
                        <p className="text-xs text-gray-500 leading-relaxed">{smsPreview}</p>
                        <p className="text-[10px] text-amber-600 mt-2 font-medium">Lien personnalisé et tracké.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button type="submit" disabled={sending || (sendMode === 'email' ? !form.email : !form.phone)}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold text-sm hover:from-violet-700 hover:to-indigo-700 transition-all shadow-md disabled:opacity-50 flex items-center justify-center gap-2">
                {sending
                  ? <><span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Envoi en cours…</>
                  : sendMode === 'email'
                    ? <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg> Envoyer l'email</>
                    : <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4"><path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg> Envoyer le SMS</>
                }
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Historique */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-slate-700 to-slate-600 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-white"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
            <h3 className="font-bold text-white text-sm">Historique des demandes</h3>
            <span className="ml-1 bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{requests.length}</span>
          </div>
          {/* Search */}
          <div className="relative hidden sm:block">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4 text-white/50 absolute left-3 top-1/2 -translate-y-1/2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input type="text" placeholder="Rechercher un nom…" value={search} onChange={e => setSearch(e.target.value)}
              className="bg-white/15 text-white placeholder-white/40 text-xs pl-9 pr-3 py-1.5 rounded-lg border border-white/20 focus:outline-none focus:bg-white/25 w-48 transition-all" />
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 px-5 py-3 border-b border-gray-100 bg-gray-50 overflow-x-auto">
          {filterTabs.map(tab => (
            <button key={tab.key} onClick={() => setFilter(tab.key)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
                filter === tab.key
                  ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-violet-300 hover:text-violet-600'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {requests.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">📭</p>
            <p className="text-sm font-medium">Aucune demande pour ce filtre</p>
            <p className="text-xs mt-1">Envoyez une première demande via le formulaire ci-dessus.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {requests.map((r: any) => {
              const st = statusOf(r);
              return (
                <div key={r.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors group">
                  {/* Avatar initiales */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {r.nom?.[0]?.toUpperCase() || '?'}
                  </div>
                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{r.nom}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {r.email && (
                        <span className="flex items-center gap-1 text-[11px] text-gray-400">
                          <svg viewBox="0 0 20 20" fill="currentColor" className={`w-3 h-3 ${r.email_sent ? 'text-violet-400' : 'text-gray-300'}`}><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>
                          {r.email}
                        </span>
                      )}
                      {r.phone && (
                        <span className="flex items-center gap-1 text-[11px] text-gray-400">
                          <svg viewBox="0 0 20 20" fill="currentColor" className={`w-3 h-3 ${r.sms_sent ? 'text-amber-400' : 'text-gray-300'}`}><path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/></svg>
                          {r.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Canaux */}
                  <div className="hidden sm:flex items-center gap-1.5">
                    {r.email_sent && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${r.email_clicked ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-violet-50 text-violet-600 border-violet-200'}`}>
                        {r.email_clicked ? '📬 Email cliqué' : '📧 Email'}
                      </span>
                    )}
                    {r.sms_sent && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${r.sms_clicked ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                        {r.sms_clicked ? '📱 SMS cliqué' : '📱 SMS'}
                      </span>
                    )}
                  </div>
                  {/* Statut */}
                  <div className="shrink-0">
                    <span className={`flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${st.cls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                      {st.label}
                    </span>
                  </div>
                  {/* Date */}
                  <span className="hidden lg:block text-[11px] text-gray-400 shrink-0">{timeAgo(r.createdAt || r.created_at)}</span>
                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {!r.has_reviewed && (
                      <button onClick={() => handleReviewed(r.id)} title="Marquer : avis laissé"
                        className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 flex items-center justify-center text-xs transition-colors">✓</button>
                    )}
                    <button onClick={() => handleDelete(r.id)} title="Supprimer"
                      className="w-7 h-7 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 flex items-center justify-center transition-colors">
                      <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}
